/* eslint-disable no-param-reassign */

const heroMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
const autoplayRetryDelays = [0, 100, 300, 700, 1200];
const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(navigator.userAgent);

function shouldAutoplayHeroVideo(video) {
  if (
    !video
    || heroMotionMQ.matches
    || video.dataset.heroAutoplayBlocked === 'true'
  ) {
    return false;
  }

  if (video.mediaControl) {
    return video.mediaControl.autoplay && !video.mediaControl.userPaused;
  }

  return video.autoplay || video.hasAttribute('autoplay');
}

function bindHeroPlaybackFallback(video) {
  if (video.dataset.heroPlaybackFallbackBound === 'true') {
    return;
  }

  video.dataset.heroPlaybackFallbackBound = 'true';

  const events = ['pointerdown', 'keydown'];
  const resumeFromUserGesture = async () => {
    events.forEach((eventName) => {
      document.removeEventListener(eventName, resumeFromUserGesture, true);
    });

    video.dataset.heroPlaybackFallbackBound = 'false';
    video.dataset.heroAutoplayBlocked = 'false';

    try {
      await video.play();
    } catch (error) {
      if (error?.name !== 'NotAllowedError' && error?.name !== 'AbortError') {
        console.warn('Hero Landing playback failed', error.message);
      }
    }
  };

  events.forEach((eventName) => {
    document.addEventListener(eventName, resumeFromUserGesture, {
      capture: true,
      once: true,
    });
  });
}

async function playHeroVideo(video) {
  if (
    !shouldAutoplayHeroVideo(video)
    || !video.paused
    || video.ended
  ) {
    return;
  }

  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');

  // preload="none" skips network until play/load; kick off fetch before play().
  if (
    video.preload === 'none'
    && video.readyState === 0
    && video.dataset.heroLoadStarted !== 'true'
  ) {
    video.dataset.heroLoadStarted = 'true';
    video.load();
  }

  if (video.mediaControl) {
    await video.mediaControl.playIfAble();
    return;
  }

  try {
    await video.play();
  } catch (error) {
    if (error?.name === 'NotAllowedError') {
      video.dataset.heroAutoplayBlocked = 'true';
      bindHeroPlaybackFallback(video);
    } else if (error?.name !== 'AbortError') {
      console.warn('Hero Landing autoplay failed', error.message);
    }
  }
}

function initHeroLandingVideoAutoplay() {
  document.querySelectorAll('.c-hero-landing').forEach((block) => {
    const video = block.querySelector('.c-hero-landing__video');

    if (!(video instanceof HTMLVideoElement) || video.dataset.heroAutoplayBound === 'true') {
      return;
    }

    video.dataset.heroAutoplayBound = 'true';

    const markPlaying = () => {
      if (video.paused || video.ended) {
        return;
      }

      const revealVideo = () => {
        if (!video.paused && !video.ended) {
          video.classList.add('is-playing');
        }
      };

      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(revealVideo);
      } else {
        window.requestAnimationFrame(revealVideo);
      }
    };

    const playVideo = () => {
      playHeroVideo(video);
    };

    // Keep persistent listeners so playback resumes after MediaControl calls load().
    video.addEventListener('canplay', playVideo);
    video.addEventListener('loadeddata', playVideo);
    video.addEventListener('playing', markPlaying);
    markPlaying();

    autoplayRetryDelays.forEach((delay) => {
      window.setTimeout(playVideo, delay);
    });
  });
}

function initHeroLandingIframeFallback() {
  document.querySelectorAll('.c-hero-landing').forEach((block) => {
    const mediaEl = block.querySelector('.c-hero-landing__media');

    if (!mediaEl) {
      return;
    }

    const iframe = mediaEl.querySelector('.c-hero-landing__video--remote');
    const poster = mediaEl.querySelector('.c-hero-landing__poster');

    if (!iframe || !poster) {
      return;
    }

    let loaded = false;

    const showPoster = () => {
      if (loaded) {
        return;
      }

      iframe.style.display = 'none';
      poster.classList.add('c-hero-landing__poster--visible');
    };

    iframe.addEventListener('load', () => {
      loaded = true;
    });

    iframe.addEventListener('error', showPoster);

    setTimeout(() => {
      if (!loaded) {
        showPoster();
      }
    }, 5000);
  });
}

function initHeroLandingSafariVisibility() {
  if (!isSafari) {
    return;
  }

  document.querySelectorAll('.c-hero-landing').forEach((block) => {
    const nextBlock = block.nextElementSibling;

    if (!nextBlock || block.dataset.safariVisibilityBound === 'true') {
      return;
    }

    let ticking = false;
    block.dataset.safariVisibilityBound = 'true';

    const updateVisibility = () => {
      ticking = false;

      block.classList.toggle(
        'c-hero-landing--safari-hidden',
        nextBlock.getBoundingClientRect().top <= 0,
      );
    };

    const requestUpdate = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateVisibility);
      }
    };

    updateVisibility();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
  });
}

function initHeroLanding() {
  initHeroLandingVideoAutoplay();
  initHeroLandingIframeFallback();
  initHeroLandingSafariVisibility();
}

function bootHeroLanding() {
  initHeroLanding();

  if (window.acf) {
    window.acf.addAction('render_block_preview/type=azds-hero-landing', () => {
      initHeroLanding();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootHeroLanding, { once: true });
} else {
  bootHeroLanding();
}
